import { useEffect, useState } from 'react'
import { today } from '@/logic/dates'
import { nowOf, type Now } from '@/logic/status'

/** Re-reads a value every minute and when the tab regains focus, so midnight and due times move what shows. */
function useTicking<T>(read: () => T, same: (a: T, b: T) => boolean): T {
  const [value, setValue] = useState(read)
  useEffect(() => {
    const tick = () => setValue((prev) => {
      const next = read()
      return same(prev, next) ? prev : next
    })
    const timer = window.setInterval(tick, 60_000)
    window.addEventListener('focus', tick)
    document.addEventListener('visibilitychange', tick)
    return () => {
      window.clearInterval(timer)
      window.removeEventListener('focus', tick)
      document.removeEventListener('visibilitychange', tick)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps
  return value
}

/** Today's local date. */
export function useToday(): string {
  return useTicking(today, (a, b) => a === b)
}

/** Today's date and the clock time to the minute, for orange and red. */
export function useNow(): Now {
  return useTicking(() => nowOf(), (a, b) => a.date === b.date && a.time === b.time)
}
